import { useEffect, useState } from 'react';

// Extend Navigator interface for iOS standalone detection
interface NavigatorStandalone extends Navigator {
  standalone?: boolean;
}

/**
 * Hook to detect if the app is running in PWA mode (standalone)
 */
export const usePWAMode = () => {
  const [isPWA, setIsPWA] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const checkPWAMode = () => {
      // Check multiple indicators for PWA mode
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const iosStandalone = (window.navigator as NavigatorStandalone).standalone === true;
      const androidTWA = document.referrer.includes('android-app://');
      const windowControlsOverlay = window.matchMedia(
        '(display-mode: window-controls-overlay)',
      ).matches;

      // Additional checks
      const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
      const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;

      // Check URL parameters for manual override
      const urlParams = new URLSearchParams(window.location.search);
      const forcePWA = urlParams.has('pwa') || urlParams.has('standalone');

      const isPWAMode =
        standalone ||
        iosStandalone ||
        androidTWA ||
        windowControlsOverlay ||
        isFullscreen ||
        isMinimalUI ||
        forcePWA;

      setIsPWA(isPWAMode);
      setIsInstalled(isPWAMode || !!(window.navigator as NavigatorStandalone).standalone);

      // console.log('PWA Mode Detection:', {
      //   standalone,
      //   iosStandalone,
      //   androidTWA,
      //   windowControlsOverlay,
      //   isFullscreen,
      //   isMinimalUI,
      //   forcePWA,
      //   result: isPWAMode,
      // });

      return isPWAMode;
    };

    // Initial check
    const currentPWAMode = checkPWAMode();

    // Listen for display mode changes
    const mediaQueries = [
      window.matchMedia('(display-mode: standalone)'),
      window.matchMedia('(display-mode: window-controls-overlay)'),
      window.matchMedia('(display-mode: fullscreen)'),
      window.matchMedia('(display-mode: minimal-ui)'),
    ];

    const handleDisplayModeChange = () => {
      checkPWAMode();
    };

    mediaQueries.forEach(mq => {
      mq.addEventListener('change', handleDisplayModeChange);
    });

    // Communicate PWA mode to service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SET_PWA_MODE',
        isPWA: currentPWAMode,
      });
    }

    // Listen for service worker messages asking for display mode
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'CHECK_DISPLAY_MODE') {
        // Respond with current PWA mode
        if (event.ports && event.ports[0]) {
          event.ports[0].postMessage({
            isPWA: checkPWAMode(),
            displayMode: getDisplayMode(),
          });
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      mediaQueries.forEach(mq => {
        mq.removeEventListener('change', handleDisplayModeChange);
      });
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  // Get current display mode
  const getDisplayMode = () => {
    if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
    if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
    if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
    if (window.matchMedia('(display-mode: window-controls-overlay)').matches)
      return 'window-controls-overlay';
    return 'browser';
  };

  return {
    isPWA,
    isInstalled,
    displayMode: getDisplayMode(),
    // Utility functions
    isStandalone: isPWA,
    isBrowser: !isPWA,
    // Manual controls
    forceRefresh: () => {
      // Force a refresh that bypasses service worker cache
      window.location.href =
        window.location.href + (window.location.search ? '&' : '?') + '_refresh=' + Date.now();
    },
    forcePWAMode: () => {
      // Add PWA parameter to URL
      const url = new URL(window.location.href);
      url.searchParams.set('pwa', '1');
      window.location.href = url.toString();
    },
  };
};
